import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { AppBar, Box, Container, Typography } from "@mui/material";
import "./CustomNavbar.scss";
import { FC, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Colors } from "../../styles/constant";
import {
  useAppDispatch,
  useAppSelector,
} from "../../redux-store/CustomReduxHooks";
import { T_ValidateSSOOldRes } from "../../features/Feature_SSO/Sso_Types";
import { E_ChannelOptions } from "../../globalTypes/GlobalTypes";
import { clearSsoSlice } from "../../features/Feature_SSO/Sso_Slice";
import { updateMedicalReportInlineFunClicked } from "../../features/Feature_HealthInsurance/HealthInsurance_Slice";
import {
  GTAScreenType,
  InAppFeatureFuncCodes,
  ONE_LOGIN_DASHBOARD,
  SCREEN_TYPE,
} from "../../utils/constants";
import {
  windowOpen,
  getCMSLink,
  getMobileOperatingSystem,
} from "../../utils/helper";
import TriggerGTAEvent from "../../utils/GTA";
import { resetSecondaryChannelSlice } from "../../features/Feature_SecondaryChannels/SecondaryChannel_Slice";
import {
  R_GeneralElementsRoutes,
  R_HFL_L3_Routes,
  R_HI_L3_Routes,
  R_HLRoutes,
  R_HomeRoutes,
  R_LI_L3_Routes,
  R_MF_L3_Routes,
} from "../../utils/Routes";
import { triggerCleverTapEvent } from "../../utils/CleverTap";
import { setHlSuccessMessages } from "../../features/Features_Cms/Feature_Cms_Slice";
import { useLazyFetchHlSuccessMessagesQuery } from "../../features/Features_Cms/Cms_Api";
import CustomHelpComponent from "../CustomHelpComponent/CustomHelpComponent";
import CloseIcon from "@mui/icons-material/Close";
import isEmpty from "lodash/isEmpty";

interface Props {
  className?: string;
  title?: string;
  fromL2Page?: boolean;
  fromL3Page?: boolean;
  isBackBtnRequired?: boolean;
  onClose?: () => void;
  isBackBtnFromSamePage?: boolean;
  backBtnCallFromSamePage?: () => void;
}

const Navbar: FC<Props> = ({
  className = "",
  title = "",
  fromL2Page = false,
  fromL3Page = false,
  isBackBtnRequired = true,
  onClose,
  isBackBtnFromSamePage,
  backBtnCallFromSamePage,
}) => {
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();
  const [fetchHlSuccessMessages] = useLazyFetchHlSuccessMessagesQuery();
  const ssoSlice = useAppSelector((state) => state.SsoSlice.ssoData);
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const isSuperApp = csAppSlice.channel === E_ChannelOptions.SUPER_APP;
  const secondaryChannelSlice = useAppSelector(
    (state) => state.SecondaryChannelSlice,
  );
  const medicalReportDownloaded = useAppSelector(
    (state) => state.HealthInsuranceSlice.medicalReportDownloaded,
  );
  const hlSuccessMessages = useAppSelector(
    (state) => state.CmsSlice.CmsHlSuccessMEssages,
  );
  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();

  const isL3RevampRoute =
    R_HFL_L3_Routes(pathname) ||
    R_MF_L3_Routes(pathname) ||
    R_HI_L3_Routes(pathname) ||
    R_LI_L3_Routes(pathname);

  const isHFLL3PageRoute = [
    R_HLRoutes.statement_and_document,
    R_HLRoutes.advance_emi_payment,
    R_HLRoutes.home_loan_change_email,
    R_HLRoutes.emi_payment,
    ``,
  ].includes(pathname);

  const [URLSearchParams] = useSearchParams();
  const action = URLSearchParams.get("action") || "";

  useEffect(() => {
    const os = csAppSlice.NativeDeviceInfo?.deviceName
      ? getMobileOperatingSystem() === "iOS"
        ? "ios"
        : "android"
      : "web";
    if (pathname.includes("/details/hl") && isEmpty(hlSuccessMessages)) {
      fetchHlSuccessMessages().then((res) => {
        if (res.isSuccess && res.data) {
          const updatedData = res.data.payload.data.map((item) => ({
            ...item,
            attributes: {
              ...item.attributes,
              link: getCMSLink(`${item.attributes.link}`, os),
            },
          }));
          appDispatch(setHlSuccessMessages(updatedData));
        }
      });
    }
  }, []);

  const backBtnFunc = () => {
    handleGTAEvent();
    if (action === "search") {
      navigate(R_HomeRoutes.home);
      return;
    }
    if (R_GeneralElementsRoutes.setMpin === pathname) {
      navigate("/", { replace: true });
      return;
    }
    if (
      csAppSlice.channel === E_ChannelOptions.SUPER_APP &&
      (ssoSlice as T_ValidateSSOOldRes)?.functionality ===
        InAppFeatureFuncCodes.HI_POLICY_RENEWAL &&
      `${pathname}${search}` ===
        (ssoSlice as T_ValidateSSOOldRes)?.urlConstructured
    ) {
      dispatch(clearSsoSlice());
      navigate(R_HomeRoutes.home, { replace: true });
      return;
    }
    if (medicalReportDownloaded) {
      dispatch(updateMedicalReportInlineFunClicked(true));
    }
    if (
      csAppSlice.channel === E_ChannelOptions.ONE_ABC &&
      `${pathname}${search}` ===
        (ssoSlice as T_ValidateSSOOldRes)?.urlConstructured
    ) {
      dispatch(clearSsoSlice());
      windowOpen(ONE_LOGIN_DASHBOARD, "_self");
    } else if (
      csAppSlice.channel === E_ChannelOptions.CSAPP &&
      `${pathname}${search}` ===
        (ssoSlice as T_ValidateSSOOldRes)?.urlConstructured
    ) {
      dispatch(clearSsoSlice());
      navigate(R_HomeRoutes.home);
    } else if (
      csAppSlice.channel === E_ChannelOptions.SECONDARY_CHANNEL &&
      `${pathname}${search}` === secondaryChannelSlice?.constructedAppUrl
    ) {
      dispatch(resetSecondaryChannelSlice());
      navigate(R_HomeRoutes.home);
    } else {
      navigate(-1);
    }
  };

  const getBackButtonHandler = () => {
    if (isBackBtnFromSamePage && backBtnCallFromSamePage) {
      return backBtnCallFromSamePage;
    }
    return backBtnFunc;
  };

  const handleGTAEvent = () => {
    if (pathname.includes("hl")) {
      triggerCleverTapEvent("back_click", {
        ScreenType: SCREEN_TYPE.HFScreenType,
      });
      TriggerGTAEvent("back_click", {
        screen_type: GTAScreenType.HFScreenType,
        screen_name: "Servicing Homescreen",
        isPrevPageNameRequired: true,
      });
    } else if (pathname.includes("li")) {
      triggerCleverTapEvent("back_click", {
        ScreenType: SCREEN_TYPE.LIScreenType,
      });
      TriggerGTAEvent("back_click", {
        screen_type: GTAScreenType.LIScreenType,
        screen_name: "Life Insurance Servicing Detail Screen",
        isPrevPageNameRequired: true,
      });
    } else if (pathname.includes("mutual-fund")) {
      triggerCleverTapEvent("back_click", {
        ScreenType: SCREEN_TYPE.MFScreenType,
      });
      TriggerGTAEvent("back_click", {
        screen_type: GTAScreenType.MFScreenType,
        screen_name: "Mutual Funds Servicing Detail Screen",
        isPrevPageNameRequired: true,
      });
    } else if (pathname.includes("my-portfolio")) {
      triggerCleverTapEvent("back_click", {
        ScreenType: SCREEN_TYPE.StockScreenType,
      });
      TriggerGTAEvent("back_click", {
        screen_type: GTAScreenType.StockScreenType,
        screen_name: "Stocks & Securities Servicing Detail Screen",
        isPrevPageNameRequired: true,
      });
    } else if (pathname.includes("hi")) {
      triggerCleverTapEvent("back_click", {
        ScreenType: SCREEN_TYPE.HIScreenType,
      });
      TriggerGTAEvent("back_click", {
        screen_type: GTAScreenType.HI_ScreenType,
        screen_name: "Health Insurance Servicing Detail Screen",
        isPrevPageNameRequired: true,
      });
    } else {
      triggerCleverTapEvent("back_click", {
        ScreenType: SCREEN_TYPE.DashboardScreenType,
      });
      TriggerGTAEvent("back_click", {
        screen_type: GTAScreenType.DashboardScreenType,
        screen_name: "Servicing Homescreen",
        isPrevPageNameRequired: true,
      });
    }
  };

  return (
    <>
      {!fromL2Page && !fromL3Page && (
        <AppBar
          className={`navbar ${isSuperApp ? "no-tap-highlights" : ""} ${isHFLL3PageRoute ? "navbar--hl-l3-page" : ""} ${className}`}
          position="static"
          sx={{
            backgroundColor: "white",
            boxShadow: "0rem .5rem 1rem 0rem #0000000A",
          }}
        >
          <Container
            sx={{ display: "flex", alignItems: "center", paddingY: ".75rem" }}
          >
            {className !== "from-loading" && (
              <Box className="nav-btn-wrapper" onClick={getBackButtonHandler()}>
                <KeyboardArrowLeftIcon className="back-arrow-icon" />
              </Box>
            )}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexGrow={1}
            >
              <Typography
                fontSize={18}
                fontWeight={isL3RevampRoute ? 600 : 500}
                lineHeight="120%"
                color="#2E2E2E"
              >
                {title}
              </Typography>
              <CustomHelpComponent />
            </Box>
          </Container>
        </AppBar>
      )}
      {fromL2Page && (
        <Box className="navbar-l2-page">
          <Box display="flex" alignItems="center" gap=".75rem">
            {isBackBtnRequired && (
              <Box
                className="nav-btn-wrapper"
                onClick={
                  isBackBtnFromSamePage ? backBtnCallFromSamePage : backBtnFunc
                }
              >
                <KeyboardArrowLeftIcon className="back-arrow-icon" />
              </Box>
            )}

            <Typography
              variant="body1"
              lineHeight="120%"
              sx={{
                fontWeight: 400,
                fontSize: "1rem",
              }}
            >
              {title}
            </Typography>
          </Box>
          <CustomHelpComponent />
        </Box>
      )}
      {fromL3Page && (
        <Box className="navbar-l3-page">
          <Box display="flex" alignItems="center" gap=".75rem">
            {isBackBtnRequired && (
              <Box
                className="nav-btn-wrapper"
                onClick={
                  isBackBtnFromSamePage ? backBtnCallFromSamePage : backBtnFunc
                }
              >
                <KeyboardArrowLeftIcon className="back-arrow-icon" />
              </Box>
            )}

            <Typography
              variant="h3"
              lineHeight="120%"
              sx={{
                color: Colors.white,
                fontWeight: 600,
                fontSize: "1.125rem",
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box className="nav-btn-wrapper" onClick={onClose}>
            <CloseIcon
              sx={{
                color: Colors.black,
                backgroundColor: Colors.white,
                fontSize: "16px",
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default Navbar;
